import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfUseModal from './TermsOfUseModal';

const StickyNavigation = () => {
  // Mobile: reduce backdrop-filter from blur(12px) → blur(4px).
  // The nav is fixed and visible the ENTIRE time the user scrolls —
  // a 12px blur on a fixed element repaints the GPU stencil buffer
  // on every scroll frame. 4px is visually equivalent and 3× cheaper.
  const isMobile = useMemo(() =>
    window.innerWidth <= 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent), []);
  const navBlur = isMobile ? 'blur(4px)' : 'blur(12px)';

  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [tickerHeight, setTickerHeight] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedMobileDropdown, setExpandedMobileDropdown] = useState(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [navInfoItems, setNavInfoItems] = useState(null);
  const [dbNavItems, setDbNavItems] = useState(null);

  // Load dynamic Info dropdown items from settings
  useEffect(() => {
    const api = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${api}/api/settings`)
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        if (data.nav_info_dropdown) {
          try { setNavInfoItems(JSON.parse(data.nav_info_dropdown)); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  // Load main navigation from the CMS (Admin-Panel → Navigation).
  // Previously these items were hardcoded and the admin section had no effect.
  useEffect(() => {
    const api = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${api}/api/navigation`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data) && data.length > 0) setDbNavItems(data);
      })
      .catch(() => {});
  }, []);

  // Navigation items configuration
  const navItems = useMemo(() => {
    const defaultInfoDropdown = [
      { id: 'vibie-mod', label: 'Vibie AI Mod', href: '#vibie-mod' },
      { id: 'team',      label: 'Team',         href: '#team' },
      { id: 'faq',       label: 'FAQ',           href: '#faq' },
      { id: 'privacy', label: 'Privacy Policy / Legal Notice', action: 'openPrivacyModal' },
      { id: 'terms', label: 'Terms of Use', action: 'openTermsModal' },
      { id: 'contact', label: 'Contact', href: 'mailto:support@solvibetoken.com', external: true }
    ];
    const infoDropdown = navInfoItems !== null ? navInfoItems : defaultInfoDropdown;

    // Fallback (CMS leer/nicht erreichbar): bisherige Standard-Links
    const defaultMain = [
      { id: 'home', label: 'Home', href: '#home' },
      { id: 'tokenomics', label: 'Tokenomics', href: '#tokenomics' },
      { id: 'community', label: 'Community', href: '#community' },
      { id: 'contract', label: 'Contract', href: '#contract' },
      { id: 'buy-trade', label: 'Buy / Trade', href: '#buy-trade' },
    ];

    // CMS-Einträge: '#anker' scrollt zur Sektion, http(s)/mailto öffnet extern
    const main = dbNavItems
      ? dbNavItems
          .filter(n => n.visible !== false && n.label && n.href)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(n => {
            const isAnchor = n.href.startsWith('#');
            return {
              id: isAnchor ? n.href.slice(1) : `ext-${n.id || n.label}`,
              label: n.label,
              href: n.href,
              external: !isAnchor,
            };
          })
      : defaultMain;

    return [
      ...main,
      {
        id: 'info',
        label: 'Info',
        href: null,
        dropdown: infoDropdown
      }
    ];
  }, [navInfoItems, dbNavItems]);

  // Calculate ticker height for proper positioning
  useEffect(() => {
    const calculateTickerHeight = () => {
      const ticker = document.querySelector('[class*="fixed top-0"]');
      if (ticker) {
        setTickerHeight(ticker.offsetHeight);
      }
    };

    calculateTickerHeight();
    window.addEventListener('resize', calculateTickerHeight);
    
    return () => window.removeEventListener('resize', calculateTickerHeight);
  }, []);

  // Handle navigation clicks
  const handleNavClick = useCallback((e, sectionId) => {
    if (sectionId === 'info' || !sectionId) return; // Skip for dropdown items

    const targetElement = document.getElementById(sectionId);
    if (!targetElement) {
      // Kein Anker auf der Seite (z.B. externer Link aus dem CMS):
      // Menüs schließen und dem Browser die normale Navigation überlassen.
      setIsMenuOpen(false);
      setOpenDropdown(null);
      setExpandedMobileDropdown(null);
      return;
    }

    e.preventDefault();
    setIsMenuOpen(false);
    setOpenDropdown(null);
    setExpandedMobileDropdown(null);

    const offsetTop = targetElement.offsetTop - tickerHeight - 64 - 20;
    window.scrollTo({
      top: Math.max(0, offsetTop),
      behavior: 'smooth'
    });
    setActiveSection(sectionId);
  }, [tickerHeight]);

  // Intersection Observer for active section tracking
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: `-${tickerHeight + 64 + 20}px 0px -50% 0px`,
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    const observerCallback = (entries) => {
      const visibleSections = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      if (visibleSections.length > 0) {
        const topSection = visibleSections[0];
        const sectionId = topSection.target.id || 'home';
        
        if (window.scrollY < 200) {
          setActiveSection('home');
        } else {
          setActiveSection(sectionId);
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    navItems.forEach(item => {
      if (item.href && item.href.startsWith('#')) {
        const element = document.getElementById(item.id);
        if (element) {
          observer.observe(element);
        }
      }
    });

    const heroSection = document.querySelector('section[class*="min-h-screen"]');
    if (heroSection && !heroSection.id) {
      heroSection.id = 'home';
      observer.observe(heroSection);
    }

    return () => observer.disconnect();
  }, [navItems, tickerHeight]);

  // Handle mobile menu
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('[data-dropdown]')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Handle dropdown action clicks (modals, etc.)
  const handleDropdownAction = useCallback((action) => {
    setOpenDropdown(null); // Close dropdown
    setExpandedMobileDropdown(null); // Close mobile dropdown
    
    switch (action) {
      case 'openPrivacyModal':
        setShowPrivacyModal(true);
        break;
      case 'openTermsModal':
        setShowTermsModal(true);
        break;
      default:
        break;
    }
  }, []);

  // Desktop Navigation Items
  const DesktopNavItem = ({ item, isActive }) => {
    if (item.dropdown) {
      return (
        <div className="relative" data-dropdown>
          <motion.button
            onClick={(e) => {
              // If item has both href and dropdown, handle the click
              if (item.href) {
                handleNavClick(e, item.id, true);
              }
              // Toggle dropdown
              setOpenDropdown(openDropdown === item.id ? null : item.id);
            }}
            className={`
              relative px-6 py-2 text-sm font-medium transition-all duration-300 flex items-center gap-1 rounded-lg
              hover:text-white focus:text-white focus:outline-none focus:ring-2 focus:ring-solana-primary focus:ring-opacity-50
              ${isActive || openDropdown === item.id ? 'text-white font-semibold' : 'text-gray-300 hover:text-gray-100'}
            `}
            style={{
              background: 'rgba(8, 15, 28, 0.4)',
              backdropFilter: navBlur,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            whileHover={{ 
              y: -1,
              textShadow: isActive || openDropdown === item.id
                ? "0px 0px 12px rgba(153, 69, 255, 0.6)" 
                : "0px 0px 8px rgba(255, 255, 255, 0.4)"
            }}
            whileTap={{ y: 0 }}
          >
            {item.label}
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.id ? 'rotate-180' : ''}`} />
          </motion.button>

          <AnimatePresence>
            {openDropdown === item.id && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute top-full right-0 mt-2 w-48 bg-gray-900/95 backdrop-blur-lg border border-gray-700/50 rounded-lg shadow-2xl overflow-hidden z-50"
              >
                {item.dropdown.map((subItem, index) => {
                  if (subItem.action) {
                    return (
                      <motion.button
                        key={subItem.id}
                        onClick={() => handleDropdownAction(subItem.action)}
                        className="w-full text-left block px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 transition-all duration-200 border-b border-gray-700/30 last:border-b-0"
                        whileHover={{ x: 4, backgroundColor: "rgba(156, 163, 175, 0.1)" }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {subItem.label}
                      </motion.button>
                    );
                  }
                  
                  return (
                    <motion.a
                      key={subItem.id}
                      href={subItem.href}
                      target={subItem.external ? '_self' : undefined}
                      onClick={(e) => {
                        setOpenDropdown(null);
                        if (subItem.href && subItem.href.startsWith('#')) {
                          handleNavClick(e, subItem.id);
                        }
                      }}
                      className="block px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 transition-all duration-200 border-b border-gray-700/30 last:border-b-0"
                      whileHover={{ x: 4, backgroundColor: "rgba(156, 163, 175, 0.1)" }}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      {subItem.label}
                    </motion.a>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }
    
    return (
      <motion.a
        href={item.href}
        onClick={(e) => handleNavClick(e, item.id)}
        className={`
          relative px-6 py-2 text-sm font-medium transition-all duration-300 rounded-lg
          hover:text-white focus:text-white focus:outline-none focus:ring-2 focus:ring-solana-primary focus:ring-opacity-50
          ${isActive 
            ? 'text-white font-semibold' 
            : 'text-gray-300 hover:text-gray-100'
          }
        `}
        style={{
          background: 'rgba(8, 15, 28, 0.4)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
        whileHover={{ 
          y: -1,
          textShadow: isActive 
            ? "0px 0px 12px rgba(153, 69, 255, 0.6)" 
            : "0px 0px 8px rgba(255, 255, 255, 0.4)"
        }}
        whileTap={{ y: 0 }}
        animate={{
          textShadow: isActive 
            ? "0px 0px 8px rgba(153, 69, 255, 0.4)" 
            : "0px 0px 0px rgba(255, 255, 255, 0)"
        }}
        transition={{ duration: 0.3 }}
      >
        {item.label}
      </motion.a>
    );
  };

  // Mobile Navigation Items
  const MobileNavItem = ({ item, isActive }) => {
    const isExpanded = expandedMobileDropdown === item.id;
    
    if (item.dropdown) {
      return (
        <div>
          <motion.button
            onClick={() => setExpandedMobileDropdown(isExpanded ? null : item.id)}
            className={`
              w-full flex items-center justify-between px-6 py-4 text-base font-medium transition-colors duration-200
              ${isExpanded 
                ? 'text-white bg-white/5 drop-shadow-lg' 
                : 'text-gray-300 hover:text-white hover:bg-white/5'
              }
            `}
            whileHover={{ x: 4 }}
            whileTap={{ x: 0 }}
          >
            {item.label}
            <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </motion.button>
          
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-gray-800/50 border-t border-gray-700/30"
              >
                {item.dropdown.map((subItem, index) => {
                  if (subItem.action) {
                    return (
                      <motion.button
                        key={subItem.id}
                        onClick={() => {
                          setExpandedMobileDropdown(null);
                          setIsMenuOpen(false);
                          handleDropdownAction(subItem.action);
                        }}
                        className="w-full text-left block px-8 py-3.5 text-sm text-gray-300 hover:text-white hover:bg-white/8 transition-colors duration-200 border-b border-gray-700/20 last:border-b-0"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ x: 8 }}
                      >
                        {subItem.label}
                      </motion.button>
                    );
                  }
                  
                  return (
                    <motion.a
                      key={subItem.id}
                      href={subItem.href}
                      target={subItem.external ? '_self' : undefined}
                      onClick={() => {
                        setExpandedMobileDropdown(null);
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left block px-8 py-3.5 text-sm text-gray-300 hover:text-white hover:bg-white/8 transition-colors duration-200 border-b border-gray-700/20 last:border-b-0"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ x: 8 }}
                    >
                      {subItem.label}
                    </motion.a>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <motion.a
        href={item.href}
        onClick={(e) => handleNavClick(e, item.id)}
        className={`
          block px-6 py-4 text-base font-medium transition-colors duration-200
          ${isActive 
            ? 'text-white bg-white/5 drop-shadow-lg' 
            : 'text-gray-300 hover:text-white hover:bg-white/5'
          }
        `}
        whileHover={{ x: 4 }}
        whileTap={{ x: 0 }}
      >
        {item.label}
      </motion.a>
    );
  };

  return (
    <>
      <nav
        className="fixed left-0 right-0 z-40"
        style={{ 
          top: `${tickerHeight}px`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-16">
            
            {/* Desktop Navigation - Centered with individual menu items */}
            <div className="hidden md:flex items-center justify-center">
              <div className="flex items-center space-x-8">
                {navItems.map((item) => (
                  <DesktopNavItem
                    key={item.id}
                    item={item}
                    isActive={activeSection === item.id}
                  />
                ))}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                className="p-2 text-gray-300 hover:text-white focus:text-white focus:outline-none transition-colors duration-200"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden bg-gray-900 border-t border-gray-700/50 overflow-hidden"
            >
              <div className="max-h-screen overflow-y-auto pb-24">
                {navItems.map((item) => (
                  <MobileNavItem
                    key={item.id}
                    item={item}
                    isActive={activeSection === item.id}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Spacer */}
      <div 
        className="w-full"
        style={{ 
          height: `${tickerHeight + (isMenuOpen ? 64 + 320 : 64)}px`
        }}
      />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />

      {/* Terms of Use Modal */}
      <TermsOfUseModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />
    </>
  );
};

export default StickyNavigation;